import { assetsHttp } from '@/lib/api/http-client';
import { SUPPRESS_ERROR_NOTIFICATION_HEADER } from '@/lib/api/interceptors/problem-details.interceptor';
import { ENV } from '@/lib/config/env';
import { AssetsService } from '@inference.engine/assets-api-client/web';
import { i18n } from '@/shared/i18n';

interface GetFileDownloadUrlOptions {
  /** Skips the global error toast when the asset no longer exists, for a caller that shows a fallback. */
  silentNotFound?: boolean;
}

/**
 * Fetches a signed download URL for the given asset.
 * Domain-agnostic — works for any feature that needs to download an asset by ID.
 */
export const getFileDownloadUrl = async (
  assetId: string,
  options: GetFileDownloadUrlOptions = {},
) => {
  const response = await AssetsService.getAssetDownloadUrl({
    client: assetsHttp,
    path: { assetId },
    ...(options.silentNotFound && {
      headers: { [SUPPRESS_ERROR_NOTIFICATION_HEADER]: '404' },
    }),
  });

  if (!response.data) {
    throw new Error(i18n.t('common:api.assets.noDownloadUrl'));
  }

  return response.data;
};

/**
 * Marks an asset as publicly accessible.
 * Domain-agnostic — promoted from features/chats
 * (templates-config-finish) so non-chats consumers (templates-config)
 * can use it without crossing the chats feature boundary.
 */
export const markAssetPublic = async (assetId: string): Promise<void> => {
  await assetsHttp.patch({ url: `/api/v1/assets/${assetId}/public` });
};

/**
 * Builds the stable, browser-reachable public URL for an asset (served from the assets API's
 * `/d/public/{assetId}` endpoint). The asset must first be made public via {@link markAssetPublic}.
 *
 * Mirrors the backend's `resolveAssetUrl` (apps/api `apply-content-entries.ts`). Used when an
 * absolute URL must be embedded directly into rendered HTML — e.g. an `<img src>` in the email
 * widget, whose HTML is shown in a sanitized `srcDoc` iframe where relative URLs do not resolve.
 */
export const buildPublicAssetUrl = (assetId: string): string =>
  `${ENV.ASSETS_API_BASE_URL.replace(/\/+$/, '')}/d/public/${assetId}`;
